"use client";

import Chevron from "@homeinstead/icons/Chevron";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import * as React from "react";
import { cn } from "../utils/classnames";

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    className={cn("border-b font-jakarta", className)}
    ref={ref}
    {...props}
  />
));
AccordionItem.displayName = "AccordionItem";

interface TriggerIconProps {
  className?: string;
  isOpen?: boolean;
}

type TriggerIconComponent = React.ComponentType<TriggerIconProps>;

type CustomTriggerProps = React.ComponentPropsWithoutRef<
  typeof AccordionPrimitive.Trigger
>;

function withCustomTrigger(TriggerIcon: TriggerIconComponent) {
  const AccordionTriggerCustom = React.forwardRef<
    React.ElementRef<typeof AccordionPrimitive.Trigger>,
    CustomTriggerProps
  >(({ className, children, ...props }, ref) => (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        className={cn(
          "group flex flex-1 items-center justify-between py-4 font-bold text-lg text-new-beginnings-base transition-all hover:underline",
          className,
        )}
        ref={ref}
        {...props}
      >
        {children}
        <TriggerIcon
          className={cn("h-4 w-4 shrink-0 transition-transform duration-200")}
          {...props}
        />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  ));

  AccordionTriggerCustom.displayName = "AccordionTriggerCustom";
  return AccordionTriggerCustom;
}

const ChevronIcon: TriggerIconComponent = ({ className }) => (
  <Chevron
    className={cn(
      className,
      "fill-new-beginnings-base group-data-[state=open]:rotate-180",
    )}
  />
);

const PlusMinusIcon: TriggerIconComponent = ({ className }) => {
  return (
    <div
      className={cn(
        "relative h-6 w-6",
        "inline-flex items-center justify-center",
        "rounded-lg bg-transparent",
        "hover:bg-new-beginnings-tints-800",
        "focus:outline focus:ring-2 focus:ring-new-beginnings-base focus:ring-offset-4",
        "min-h-10 min-w-10 transition-colors duration-200 ease-in-out",
        className,
      )}
    >
      <div className="relative h-3 w-3">
        <span
          className={cn(
            "absolute inset-0 bg-new-beginnings-base",
            "transition-all duration-300 ease-in-out",
            "group-data-[state=open]:rotate-90",
            "left-1/2 -ml-px w-[2px]",
          )}
        />
        <span
          className={cn(
            "absolute inset-0 bg-new-beginnings-base",
            "transition-all duration-300 ease-in-out",
            "group-data-[state=closed]:opacity-100",
            "top-1/2 -mt-px h-[2px]",
          )}
        />
      </div>
    </div>
  );
};

const AccordionTriggerChevron = withCustomTrigger(ChevronIcon);
const AccordionTriggerPlusMinus = withCustomTrigger(PlusMinusIcon);

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    className="group overflow-hidden font-jakarta text-md text-new-beginnings-base transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    ref={ref}
    {...props}
  >
    <div className={cn("pt-0 pb-6 leading-7", className)}>{children}</div>
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTriggerChevron,
  AccordionTriggerPlusMinus,
};
